import { pdf } from '@react-pdf/renderer'
import React, { useState, useCallback } from 'react'

import FormPDF, { registerFormPdfFonts } from './create-pdf-from-form'
import { resolveFormFileName } from './resolve-form-file-name'
import { useClinic } from '~contexts/clinic-context'
import {
    ClientFormFieldsFragment,
    FormElementInput,
    useGetPdfUrlForClientFormLazyQuery,
    useGetPdfUrlForFormLazyQuery,
} from '~graphql/generated/graphql'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { getTemplateMapper } from '~utils/replace-template-variables'
import {
    hasRichTextFormatting,
    isFormRichTextElementType,
} from '~utils/form-rich-text'

export function useDownloadClientFormPDF(
    clientForm:
        | Pick<
              Partial<ClientFormFieldsFragment>,
              | 'id'
              | 'form_data'
              | 'client'
              | 'patient'
              | 'form'
              | 'client_completed_at'
              | 'appointment'
          >
        | undefined,
    options?: {
        fileNameOverride?: string
        showClientInfoSection?: boolean
    },
) {
    const { clinic } = useClinic()
    const { onCompleted, onError } = useToastFeedback(
        '',
        'There was an error generating the PDF. Please reload the page and try again.',
    )

    const [getPdfUrlForClientForm, { loading }] =
        useGetPdfUrlForClientFormLazyQuery()
    const [getPdfUrlForForm, { loading: loadingGetPdfUrlForForm }] =
        useGetPdfUrlForFormLazyQuery()

    const [generating, setGenerating] = useState(false)

    const handleDownloadPDF = useCallback(async () => {
        if (!clientForm?.form?.id && !clientForm?.id) {
            onError()
            return
        }

        setGenerating(true)
        try {
            let url = ''
            const formElements = clientForm.form_data?.fields || []
            const templateMapper = getTemplateMapper({
                client: clientForm.client,
                clinic: clinic || clientForm.appointment?.clinic,
                patient: clientForm.patient,
                appointment: clientForm.appointment,
            })
            const formName = clientForm.form?.name || 'Untitled Form'
            const resolvedFormName = replaceFormTemplateVariables(
                formName,
                templateMapper,
            )
            const resolvedFormElements = resolveFormElementMergeTags(
                formElements,
                templateMapper,
            )
            const shouldGenerateClientSide =
                MERGE_TAG_REGEX.test(formName) ||
                (formElements.length > 0 &&
                    formElements.some(
                        element =>
                            hasFormElementMergeTags(element) ||
                            hasFormElementRichTextFormatting(element),
                    ))

            // Wait for the next tick to help avoid reusing a cached instance
            // await new Promise(resolve => setTimeout(resolve, 0))

            let blob: Blob | undefined

            if (shouldGenerateClientSide) {
                const refreshToken = `FormPDF-${clientForm.id || clientForm.form?.id}-${Date.now()}`
                registerFormPdfFonts()
                blob = await pdf(
                    <FormPDF
                        refreshToken={refreshToken}
                        elements={resolvedFormElements}
                        formName={resolvedFormName}
                        logoSrc={clinic?.avatar_url || ''}
                        client={clientForm.client}
                        showClientInfo={
                            options?.showClientInfoSection ?? !!clientForm.id
                        }
                        clinic={clinic}
                        patient={clientForm.patient}
                    />,
                ).toBlob()
            } else if (clientForm?.id) {
                const { data } = await getPdfUrlForClientForm({
                    variables: {
                        clientFormId: clientForm.id || '',
                    },
                })
                url = data?.getPdfUrlForClientForm || ''
            } else if (clientForm?.form?.id) {
                const { data } = await getPdfUrlForForm({
                    variables: {
                        formId: clientForm.form.id || '',
                    },
                })
                url = data?.getPdfUrlForForm || ''
            }

            if (!blob && !url) {
                onError()
                return
            }

            // Fetch the PDF as a blob to ensure it downloads instead of navigating
            if (!blob) {
                const response = await fetch(url)
                if (!response.ok) {
                    onError()
                    return
                }
                blob = await response.blob()
            }

            const blobUrl = URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = blobUrl
            link.download =
                options?.fileNameOverride ||
                resolveFormFileName(clientForm.form?.form_file_naming_template, {
                    formName: clientForm.form?.name,
                    clientFirstName: clientForm.client?.first_name,
                    clientLastName: clientForm.client?.last_name,
                    patientName: clientForm.patient?.name,
                    appointmentStartTime: clientForm.appointment?.start_time,
                    clientCompletedAt: clientForm.client_completed_at,
                })
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0)
        } catch (error) {
            console.error('Error generating PDF:', error)
            onError()
        } finally {
            setGenerating(false)
        }
    }, [
        clinic,
        clientForm,
        getPdfUrlForClientForm,
        getPdfUrlForForm,
        onError,
        options?.fileNameOverride,
        options?.showClientInfoSection,
    ])

    return {
        handleDownloadPDF,
        generating: generating || loading || loadingGetPdfUrlForForm,
    }
}

const MERGE_TAG_REGEX = /{{\s*[\w_]+\s*}}/

function hasFormElementMergeTags(element: FormElementInput): boolean {
    return (
        MERGE_TAG_REGEX.test(element.content || '') ||
        MERGE_TAG_REGEX.test(element.name || '') ||
        MERGE_TAG_REGEX.test(element.value || '') ||
        !!element.options?.some(option => MERGE_TAG_REGEX.test(option))
    )
}

function hasFormElementRichTextFormatting(element: FormElementInput): boolean {
    return (
        isFormRichTextElementType(element.type) &&
        hasRichTextFormatting(element.content)
    )
}

function resolveFormElementMergeTags(
    elements: FormElementInput[],
    templateMapper: Record<string, string>,
): FormElementInput[] {
    return elements.map(element => ({
        ...element,
        content: replaceFormTemplateVariables(element.content, templateMapper),
        name: replaceFormTemplateVariables(element.name, templateMapper),
        value: replaceFormTemplateVariables(element.value, templateMapper),
        options: element.options?.map(
            option => replaceFormTemplateVariables(option, templateMapper),
        ),
    }))
}

function replaceFormTemplateVariables(
    value: string | undefined | null,
    templateMapper: Record<string, string>,
): string {
    if (!value) return ''

    return Object.keys(templateMapper).reduce((prev, key) => {
        const keyExp = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
        const replacement = MERGE_TAG_REGEX.test(templateMapper[key])
            ? ''
            : templateMapper[key] || ''
        return prev.replace(keyExp, replacement)
    }, value)
}
